import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Stack,
  Paper,
  IconButton,
  Tooltip,
  Fade,
  Chip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../store";
import DeerCard from "../../components/DeerCard";
import "./Inventory.css";
import "../Tools.css"; // Import wood-themed styling
import UpgradeModal from "./UpgradeModal"; // Import UpgradeModal
import { DeerStake, Reload,LevelupBoost, DeerUpgrade } from "../../store/user/actions";
import { toast } from "react-toastify";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SortIcon from "@mui/icons-material/Sort";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PetsIcon from "@mui/icons-material/Pets";
import StarsIcon from "@mui/icons-material/Stars";

const uri_prefix = "https://ipfs.io/ipfs/";

const filters = ["fawn", "og.deer", "abyssal", "promo"];

const filterOptions = [
  { value: "all", label: "All Types" },
  { value: "og.deer", label: "OG Deers" },
  { value: "fawn", label: "Fawn Deers" },
  { value: "abyssal", label: "Abyssal Deers" },
  { value: "promo", label: "Promo Deers" },
];

const sortOptions = [
  { value: "level_desc", label: "Level (Highest First)" },
  { value: "level_asc", label: "Level (Lowest First)" },
  { value: "name_asc", label: "Name (A-Z)" },
  { value: "name_desc", label: "Name (Z-A)" },
  { value: "status", label: "Status (Available First)" },
];

export const Inventory = () => {
  const userModule = useSelector((state: AppState) => state.userModule);
  const loginInfo = userModule.loginInfo;
  const [nfts, setNFTs] = useState<any[]>([]);
  const [filteredNfts, setFilteredNfts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [sortBy, setSortBy] = useState("status");
  const [selectedNFTForModal, setSelectedNFTForModal] = useState<any | null>(
    null
  ); // State for selected NFT
  const [isModalOpen, setIsModalOpen] = useState(false); // State to control modal visibility
  const [isUpgrading, setIsUpgrading] = useState(false); // State to track upgrade progress
  const [showInstructions, setShowInstructions] = useState(false); // State to control instructions visibility
  const dispatch = useDispatch<any>();

  useEffect(() => {
    if (loginInfo.assets && loginInfo.assets.length > 0) {
      setLoading(true);
      setNFTs(loginInfo.assets);
      applyFiltersAndSort(loginInfo.assets, filterType, sortBy);
      setLoading(false);
    } else {
      setNFTs([]);
      setFilteredNfts([]);
    }
  }, [loginInfo.assets, filterType, sortBy]);

  const handleStakeAll = () => {
    const stakeableNfts = filteredNfts.filter(
      (item: any) => !item.stakedConfig
    );
    var assetIds = stakeableNfts.map((item: any) => item.asset_id);
    const maxCount = 100;
    assetIds.splice(maxCount);

    dispatch(DeerStake(assetIds));
    toast.success(`Staked ${assetIds.length} Deers..`);
    dispatch(Reload());
  };

  const handleStake = (nft: any) => {
    const assetIds = [nft.asset_id];

    dispatch(DeerStake(assetIds));
  };

  const applyFiltersAndSort = (
    currentNfts: any[],
    filterValue: string,
    sortValue: string
  ) => {
    let _filteredNfts = [
      ...currentNfts?.filter((item: any) => item.deer_config),
    ];

    if (filterValue !== "all") {
      _filteredNfts = _filteredNfts.filter(
        (item: any) => item.schema.schema_name === filterValue
      );
    }

    _filteredNfts.sort((a: any, b: any) => {
      switch (sortValue) {
        case "level_desc":
          return b.level - a.level;
        case "level_asc":
          return a.level - b.level;
        case "name_asc":
          return a.data.name.localeCompare(b.data.name);
        case "name_desc":
          return b.data.name.localeCompare(a.data.name);
        case "status":
          const timeA = a.exploration_time || Infinity;
          const timeB = b.exploration_time || Infinity;
          return timeA - timeB;
        default:
          return 0;
      }
    });
    setFilteredNfts(_filteredNfts);
  };

  const handleFilterChange = (event: any) => {
    const filterValue = event.target.value as string;
    setFilterType(filterValue);
    applyFiltersAndSort(nfts, filterValue, sortBy);
  };

  const handleSortChange = (event: any) => {
    const sortValue = event.target.value as string;
    setSortBy(sortValue);
    applyFiltersAndSort(nfts, filterType, sortValue);
  };

  const openUpgradeModal = (nft: any) => {
    setSelectedNFTForModal(nft);
    setIsModalOpen(true);
  };

  const closeUpgradeModal = () => {
    setIsModalOpen(false);
    setSelectedNFTForModal(null); // Clear selected NFT when modal closes
  };

  const handleUpgradeConfirm = async () => {
    if (!selectedNFTForModal) return;
    
    setIsUpgrading(true);
    try {
      // Here you'd implement the actual upgrade logic
      // For example:
      // await dispatch(UpgradeNFT(selectedNFTForModal.asset_id));
      
      // Mock delayed response
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast.success("NFT upgraded successfully!");
      closeUpgradeModal();
    } catch (error) {
      console.error("Upgrade failed:", error);
      toast.error("Failed to upgrade NFT. Please try again.");
    } finally {
      setIsUpgrading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        position: "relative",
        minHeight: "calc(100vh - 76px)",
        padding: "24px",
        overflow: "hidden"
      }}
    >
      {/* Wood texture overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(45deg, rgba(92, 60, 16, 0.2) 25%, transparent 25%, transparent 50%, rgba(92, 60, 16, 0.2) 50%, rgba(92, 60, 16, 0.2) 75%, transparent 75%, transparent)",
          backgroundSize: "10px 10px",
          opacity: 0.3,
          zIndex: 0,
        }}
      />

      <Box className="inventory-container" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            mb: 4, 
            background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
            backgroundSize: '100% 100%',
            borderRadius: '16px',
            position: 'relative', 
            overflow: 'hidden',
            border: '4px solid #7d5014',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)'
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, alignItems: {xs: 'center', sm: 'flex-start'}, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: {xs: 2, sm: 0} }}>
              <PetsIcon sx={{ color: "#f5e1a3", mr: 2, fontSize: "2.5rem" }} />
              <Box>
                <Typography 
                  variant="h4" 
                  component="h1" 
                  sx={{ 
                    color: "#f5e1a3", 
                    fontWeight: "bold",
                    textShadow: '2px 2px 4px rgba(0,0,0,0.6)'
                  }}
                >
                  Deer Inventory
                </Typography>
                <Typography variant="body1" color="rgba(255,255,255,0.9)" sx={{ mt: 0.5, textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
                  Manage, upgrade and stake your deer collection
                </Typography>
              </Box>
            </Box>
            
            <Button
              variant="outlined"
              startIcon={<HelpOutlineIcon />}
              onClick={() => setShowInstructions(true)}
              sx={{
                color: "#f5e1a3",
                borderColor: "#f5e1a3",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(245, 225, 163, 0.1)",
                },
                alignSelf: {xs: 'center', sm: 'flex-start'}
              }}
            >
              Instructions
            </Button>
          </Box>
          
          {/* Wood grain overlay */}
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0,
            background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
            opacity: 0.4, 
            zIndex: 1 
          }} />
          
          {/* Decorative element */}
          <Box sx={{ 
            position: 'absolute', 
            right: {xs: -30, md: 30}, 
            top: 0, 
            opacity: 0.15, 
            zIndex: 1, 
            transform: 'rotate(-10deg)' 
          }}>
            <PetsIcon sx={{ fontSize: 120 }} />
          </Box>
        </Paper>

        {/* Instructions Panel */}
        {showInstructions && (
          <Fade in={showInstructions}>
            <Paper 
              elevation={3} 
              className="game-paper"
              sx={{
                position: "relative",
                zIndex: 1,
                mb: 3,
                borderRadius: "12px",
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="h6" sx={{ color: "#442a0c", fontWeight: "bold", mb: 1 }}>
                  Inventory Management Instructions
                </Typography>
                <IconButton 
                  onClick={() => setShowInstructions(false)}
                  size="small"
                  sx={{ color: "#442a0c" }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              
              <Typography variant="body1" paragraph sx={{ color: "#442a0c" }}>
                Welcome to your Deer Inventory! Here you can manage all your Deer NFTs.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ color: "#442a0c", fontWeight: "bold", mb: 1 }}>
                    Managing your inventory:
                  </Typography>
                  <ul style={{ marginLeft: "20px", color: "#442a0c" }}>
                    <li>Filter your deer by type using the dropdown menu</li>
                    <li>Sort your collection by level, name, or availability</li>
                    <li>Click on a deer to see its details and upgrade options</li>
                    <li>Use the "Stake All" button to stake multiple deer at once</li>
                  </ul>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ color: "#442a0c", fontWeight: "bold", mb: 1 }}>
                    Pro tips:
                  </Typography>
                  <ul style={{ marginLeft: "20px", color: "#442a0c" }}>
                    <li>Higher level deer earn better rewards during exploration</li>
                    <li>Different deer types have different exploration bonuses</li>
                    <li>Upgrade your deer to increase their earning potential</li>
                  </ul>
                </Grid>
              </Grid>
            </Paper>
          </Fade>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Box className="stats-card" sx={{ 
              p: 2, 
              height: "auto",
              minHeight: "120px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center"
            }}>
              <Typography variant="subtitle1" sx={{ color: "#f5e1a3", mb: 1, fontWeight: "bold" }}>
                Total Deer
              </Typography>
              <Typography variant="h5" sx={{ color: "white", fontWeight: "bold" }}>
                {nfts.length}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box className="stats-card" sx={{ 
              p: 2, 
              height: "auto",
              minHeight: "120px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center"
            }}>
              <Typography variant="subtitle1" sx={{ color: "#f5e1a3", mb: 1, fontWeight: "bold" }}>
                Available Deer
              </Typography>
              <Typography variant="h5" sx={{ color: "white", fontWeight: "bold" }}>
                {nfts.filter((item: any) => !item.stakedConfig && !item.exploration_time).length}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box className="stats-card" sx={{ 
              p: 2, 
              height: "auto",
              minHeight: "120px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center"
            }}>
              <Typography variant="subtitle1" sx={{ color: "#f5e1a3", mb: 1, fontWeight: "bold" }}>
                Staked Deer
              </Typography>
              <Typography variant="h5" sx={{ color: "white", fontWeight: "bold" }}>
                {nfts.filter((item: any) => item.stakedConfig).length}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box className="stats-card" sx={{ 
              p: 2, 
              height: "auto",
              minHeight: "120px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center"
            }}>
              <Typography variant="subtitle1" sx={{ color: "#f5e1a3", mb: 1, fontWeight: "bold" }}>
                Exploring Deer
              </Typography>
              <Typography variant="h5" sx={{ color: "white", fontWeight: "bold" }}>
                {nfts.filter((item: any) => item.exploration_time).length}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Filter and Action Bar */}
        <Box className="wood-panel" sx={{ p: 2, borderRadius: "12px" }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 2, sm: 3 }}
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <FilterAltIcon sx={{ color: "#f5e1a3" }} />
              <FormControl variant="outlined" sx={{ minWidth: 180 }}>
                <InputLabel id="nft-filter-label" sx={{ color: "#f5e1a3" }}>
                  Filter by Type
                </InputLabel>
                <Select
                  labelId="nft-filter-label"
                  id="nft-type-filter"
                  value={filterType}
                  onChange={handleFilterChange}
                  label="Filter by Type"
                  sx={{ 
                    color: "white",
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: '#7d5014',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5e1a3',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5e1a3',
                    },
                    '.MuiSvgIcon-root': {
                      color: '#f5e1a3',
                    }
                  }}
                >
                  {filterOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Button
              variant="contained"
              className="action-button"
              sx={{
                bgcolor: "#5c3c10",
                color: "#f5e1a3",
                fontWeight: "bold",
                borderRadius: "8px",
                padding: "10px 20px",
                textTransform: "none",
                "&:hover": { 
                  bgcolor: "#4a300b",
                },
                "&.Mui-disabled": {
                  bgcolor: "rgba(92, 60, 16, 0.4)",
                  color: "rgba(245, 225, 163, 0.4)",
                }
              }}
              onClick={handleStakeAll}
              disabled={
                filteredNfts.filter((item: any) => !item.stakedConfig).length ===
                0
              }
            >
              <StarsIcon sx={{ mr: 1 }} />
              Stake All (
              {
                filteredNfts
                  .filter((item: any) => !item.stakedConfig)
                  ?.slice(0, 100).length
              })
            </Button>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <SortIcon sx={{ color: "#f5e1a3" }} />
              <FormControl variant="outlined" sx={{ minWidth: 180 }}>
                <InputLabel id="nft-sort-label" sx={{ color: "#f5e1a3" }}>
                  Sort By
                </InputLabel>
                <Select
                  labelId="nft-sort-label"
                  id="nft-sort-select"
                  value={sortBy}
                  onChange={handleSortChange}
                  label="Sort By"
                  sx={{ 
                    color: "white",
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: '#7d5014',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5e1a3',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5e1a3',
                    },
                    '.MuiSvgIcon-root': {
                      color: '#f5e1a3',
                    }
                  }}
                >
                  {sortOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Box>

        {/* Loading indicator */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
            <CircularProgress sx={{ color: "#f5e1a3" }} />
          </Box>
        )}

        {/* NFT Grid Container */}
        <Box
          className="wood-panel"
          sx={{ 
            borderRadius: "16px",
            p: 3,
            mt: 3,
            position: "relative",
            zIndex: 2
          }}
        >
          <Typography 
            variant="h5" 
            sx={{ 
              color: "#f5e1a3", 
              mb: 3, 
              fontWeight: "bold",
              textAlign: "center",
              borderBottom: "2px solid rgba(245, 225, 163, 0.3)",
              pb: 1
            }}
          >
            {filterType !== "all" 
              ? `${filterOptions.find(opt => opt.value === filterType)?.label}` 
              : "All Deer NFTs"}
            <Chip 
              label={`${filteredNfts.length} Found`} 
              size="small" 
              sx={{ 
                ml: 2, 
                bgcolor: "rgba(245, 225, 163, 0.2)", 
                color: "#f5e1a3",
                border: "1px solid #7d5014"
              }} 
            />
          </Typography>
          
          <Box
            sx={{ 
              overflowY: "auto", 
              maxHeight: "60vh",
              p: 1,
              position: "relative",
              zIndex: 3,
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(245, 225, 163, 0.3) rgba(92, 60, 16, 0.1)",
              "&::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(92, 60, 16, 0.1)",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(245, 225, 163, 0.3)",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(245, 225, 163, 0.5)",
              },
            }}
          >
            <Grid
              container
              spacing={{ xs: 2, sm: 2, md: 3 }}
              columns={{ xs: 12, sm: 12, md: 12 }}
              sx={{ position: "relative", zIndex: 4 }}
            >
              {loading && nfts.length === 0 ? (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress sx={{ color: "#f5e1a3" }} />
                  </Box>
                </Grid>
              ) : filteredNfts.length > 0 ? (
                filteredNfts.map((nft, index) => (
                    <Box
                    key={nft.asset_id}
                    sx={{
                      flex: "1 1 200px",
                      maxWidth: { xs: "100%", sm: "48%", md: "23%", lg: "16%" },
                      minWidth: "180px",
                      m: 1,
                      boxSizing: "border-box",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "stretch",
                    }}
                    >
                    <Box
                      sx={{
                      width: "100%",
                      maxWidth: "200px",
                      background: nft.exploration_time
                      ? "linear-gradient(135deg, #3d2c0a 0%, #5c3c10 100%)"
                      : nft.stakedConfig
                      ? "linear-gradient(135deg, #2c3e0a 0%, #476310 100%)"
                      : "linear-gradient(135deg, #5c3c10 0%, #7d5014 100%)",
                      borderRadius: "8px",
                      p: 1,
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 12px 24px rgba(0, 0, 0, 0.35)",
                      },
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "stretch",
                      }}
                    >
                      <DeerCard
                        uri={uri_prefix + nft.data.img}
                        nftData={nft}
                        name={nft.data.name}
                        assetID={nft.asset_id}
                        stakedTime={nft.exploration_time}
                        onUpgrade={() => openUpgradeModal(nft)}
                        onStake={() => handleStake(nft)}
                        upgradeTime={nft.stakedConfig?.upgrade_end_at}
                      />
                    </Box>
                    </Box>
                ))
              ) : (
                !loading && (
                  <Grid item xs={12}>
                    <Box 
                      sx={{ 
                        textAlign: "center", 
                        p: 4,
                        borderRadius: "8px",
                        backgroundColor: "rgba(92, 60, 16, 0.2)",
                        border: "1px dashed #7d5014"
                      }}
                    >
                      <PetsIcon sx={{ fontSize: 60, color: "rgba(245, 225, 163, 0.3)", mb: 2 }} />
                      <Typography variant="h6" sx={{ color: "#f5e1a3" }}>
                        No Deer NFTs found in your inventory{" "}
                        {filterType !== "all"
                          ? `of type "${filterOptions
                              .find((opt) => opt.value === filterType)
                              ?.label.toLowerCase()
                              .replace(" deers", "")}"`
                          : ""}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "rgba(245, 225, 163, 0.7)", mt: 1 }}>
                        Try changing your filter settings or acquire some NFTs
                      </Typography>
                    </Box>
                  </Grid>
                )
              )}
            </Grid>
          </Box>
        </Box>
      </Box>
      
      {/* Render UpgradeModal conditionally */}
      <UpgradeModal
        open={isModalOpen}
        onClose={closeUpgradeModal}
        nft={selectedNFTForModal}
        onConfirm={handleUpgradeConfirm}
        isUpgrading={isUpgrading}
        type="deer"
      />
    </Box>
  );
};
